import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaBriefcase,
  FaCheckCircle,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import api from "../services/api";
import AuthPageLogo from "../components/AuthPageLogo";
import FounderAccessHeroPanel from "../components/auth/FounderAccessHeroPanel";
import "../assets/styles/login-page.scss";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Invitation token is missing from this link.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/access-invitation", {
          params: { token },
        });
        if (cancelled) return;
        const preview = res.data?.data?.invitation || null;
        setInvitation(preview);
        if (preview?.alreadyAccepted) {
          setAccepted(true);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              "This invitation link is invalid or has already been used.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error("Invitation token is missing.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/access-invitation/accept", {
        token,
        password,
        confirmPassword,
      });
      setAccepted(true);
      setPassword("");
      setConfirmPassword("");
      toast.success("Invitation accepted. You can now sign in.");
      navigate("/login", { replace: true });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to accept invitation.";
      toast.error(message);
      if (/already accepted/i.test(message)) {
        setAccepted(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-page login-page--split">
      <div className="login-page__shell login-page__shell--split">
        <FounderAccessHeroPanel />

        <main className="login-page__form-panel login-page__form-panel--split">
          <div className="login-page__form-inner">
            <div className="login-page__form-logo">
              <AuthPageLogo />
            </div>
            <h2 className="login-page__form-title">
              {accepted ? "Account activated" : "Activate your account"}
            </h2>
            <p className="login-page__form-subtitle">
              {accepted
                ? "Your invitation is complete. Sign in with your new password."
                : "Accept your invitation and create a secure password"}
            </p>

            {loading ? (
              <div className="text-center py-4 text-muted">
                Loading your invitation...
              </div>
            ) : accepted ? (
              <div className="text-center py-2">
                <p className="text-muted mb-3">
                  {invitation?.email
                    ? `Your account (${invitation.email}) is ready.`
                    : "Your account is ready."}
                </p>
                <Link to="/login" className="login-page__submit">
                  Go to login
                </Link>
              </div>
            ) : loadError || !invitation ? (
              <div className="text-center py-3">
                <p className="text-muted mb-3">
                  {loadError ||
                    "This invitation link is invalid or has already been used."}
                </p>
                <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                  If you already activated your account, please sign in.
                </p>
                <Link to="/login" className="login-page__submit">
                  Go to login
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <div className="login-page__field">
                  <label
                    className="login-page__label"
                    htmlFor="invitation-email"
                  >
                    Email address
                  </label>
                  <div className="login-page__input-wrap">
                    <FaEnvelope
                      className="login-page__input-icon"
                      aria-hidden
                    />
                    <input
                      id="invitation-email"
                      className="login-page__input login-page__input--tinted"
                      value={invitation.email}
                      disabled
                    />
                  </div>
                </div>

                <div className="login-page__field">
                  <label
                    className="login-page__label"
                    htmlFor="invitation-role"
                  >
                    Assigned role
                  </label>
                  <div className="login-page__input-wrap">
                    <FaBriefcase
                      className="login-page__input-icon"
                      aria-hidden
                    />
                    <input
                      id="invitation-role"
                      className="login-page__input login-page__input--tinted"
                      value={invitation.role || "Platform User"}
                      disabled
                    />
                  </div>
                </div>

                <div className="login-page__field">
                  <label
                    className="login-page__label"
                    htmlFor="invitation-password"
                  >
                    Create password
                  </label>
                  <div className="login-page__input-wrap">
                    <FaLock className="login-page__input-icon" aria-hidden />
                    <input
                      id="invitation-password"
                      type={showPassword ? "text" : "password"}
                      className="login-page__input login-page__input--tinted"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Minimum 8 characters"
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-page__input-toggle"
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="login-page__field">
                  <label
                    className="login-page__label"
                    htmlFor="invitation-confirm-password"
                  >
                    Confirm password
                  </label>
                  <div className="login-page__input-wrap">
                    <FaLock className="login-page__input-icon" aria-hidden />
                    <input
                      id="invitation-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      className="login-page__input login-page__input--tinted"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      placeholder="Re-enter your password"
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-page__input-toggle"
                      onClick={() =>
                        setShowConfirmPassword((visible) => !visible)
                      }
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirmed password"
                          : "Show confirmed password"
                      }
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-page__submit"
                  disabled={saving}
                >
                  <FaCheckCircle aria-hidden />
                  {saving ? "Activating..." : "Activate Account"}
                </button>

                <p className="login-page__signup">
                  Already activated? <Link to="/login">Sign in</Link>
                </p>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
